import { useState } from 'react';
import { planPrice } from '@/config/plans';
import { X, Crown, Check, Sparkles, Calendar, Zap, Brain, Bookmark, Info, Search, Pencil, Archive } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (plan: 'trip_pass' | 'monthly' | 'yearly') => Promise<boolean>;
  currentDayGroups: number;
  currentLocationGroups: number;
  maxFreeDayGroups: number;
  maxFreeLocationGroups: number;
  /** When true, hide the paid-plan grid and offer free Beta activation
   *  instead — mirrors ProScreen's Beta banner. Activation grants Yearly
   *  free via handleUpgrade's Beta branch (see App.tsx:712-730). */
  isBeta?: boolean;
}

export function PaymentModal({
  isOpen,
  onClose,
  onUpgrade,
  currentDayGroups,
  currentLocationGroups,
  maxFreeDayGroups,
  maxFreeLocationGroups,
  isBeta = false
}: PaymentModalProps) {
  const { t } = useLanguage();
  const [selectedPlan, setSelectedPlan] = useState<'trip_pass' | 'monthly' | 'yearly'>('yearly');
  const [processing, setProcessing] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    setProcessing(true);
    setFailed(false);
    const success = await onUpgrade(selectedPlan);
    setProcessing(false);
    if (success) {
      onClose();
    } else {
      setFailed(true);
    }
  };

  const exceededDays = currentDayGroups >= maxFreeDayGroups;
  const exceededLocations = currentLocationGroups >= maxFreeLocationGroups;
  const showLimitAlert = exceededDays || exceededLocations;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-labelledby="payment-modal-title">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        role="presentation"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-gradient-to-b from-[var(--bg-card)] to-[var(--bg-primary)] border border-[var(--accent-gold)]/40 shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[var(--accent-gold)]/10 to-transparent pointer-events-none" />

        <button
          onClick={onClose}
          aria-label="Close payment modal"
          className="absolute top-4 right-4 z-10 p-2 rounded-lg hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[var(--accent-gold)] to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 id="payment-modal-title" className="text-lg font-semibold tracking-tight text-[var(--text-primary)] leading-tight">
                {t.payment?.unlockUnlimited || 'Unlock Unlimited Trips'}
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                {t.payment?.upgradeDescription || 'Save as many trips as you want, organized your way'}
              </p>
            </div>
          </div>

          {/* Limit reached alert */}
          {showLimitAlert && (
            <div className="mb-6 p-4 rounded-xl bg-[var(--status-red-bg)] border border-[var(--status-red)]/40">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <Zap className="w-5 h-5 text-[var(--status-red)]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--status-red)] mb-1">
                    {t.payment?.limitReached || 'Group Limit Reached'}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {exceededDays && exceededLocations ? (
                      t.payment?.bothLimitsReached?.replace('{maxDays}', String(maxFreeDayGroups)).replace('{maxLocations}', String(maxFreeLocationGroups)) ||
                      `You have ${maxFreeDayGroups} day groups and ${maxFreeLocationGroups} location groups`
                    ) : exceededDays ? (
                      t.payment?.dayLimitReached?.replace('{current}', String(currentDayGroups)).replace('{max}', String(maxFreeDayGroups)) ||
                      `You have ${currentDayGroups} of ${maxFreeDayGroups} day groups`
                    ) : (
                      t.payment?.locationLimitReached?.replace('{current}', String(currentLocationGroups)).replace('{max}', String(maxFreeLocationGroups)) ||
                      `You have ${currentLocationGroups} of ${maxFreeLocationGroups} location groups`
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* What you get */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-[var(--accent-gold)]" />
              <span className="text-xs font-medium text-[var(--text-primary)]">
                {t.payment?.whatYouGet || 'What you get'}
              </span>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <Check className="w-3 h-3 text-[var(--accent-teal)] flex-shrink-0" />
                <Search className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
                <span>Unlimited interest searches</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <Check className="w-3 h-3 text-[var(--accent-teal)] flex-shrink-0" />
                <Pencil className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
                <span>Saved Interests — personalise every search</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <Check className="w-3 h-3 text-[var(--accent-teal)] flex-shrink-0" />
                <Brain className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
                <span>{t.payment?.aiHighMode || 'AI High — extra passes & deeper taste matching'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <Check className="w-3 h-3 text-[var(--accent-teal)] flex-shrink-0" />
                <Calendar className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
                <span>{t.payment?.unlimitedDayGroups || 'Unlimited day/location groups'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <Check className="w-3 h-3 text-[var(--accent-teal)] flex-shrink-0" />
                <Bookmark className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
                <span>{t.payment?.saveUnlimitedTrips || 'Save unlimited trips forever'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <Check className="w-3 h-3 text-[var(--accent-teal)] flex-shrink-0" />
                <Archive className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
                <span>Unlock Time Archived Trips</span>
              </div>
            </div>
          </div>

          {/* Beta banner — mirrors ProScreen. When BETA_MODE is on (globally
              or per-user), the paid plans are hidden and "Activate Free Beta"
              grants Yearly free via handleUpgrade's Beta branch. */}
          {isBeta && (
            <div className="mb-4 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-center">
              <p className="text-sm font-semibold text-indigo-400">
                {t.payment?.betaBannerTitle || 'Beta Access — All Pro features are free'}
              </p>
              <p className="text-xs text-indigo-400/70 mt-0.5">
                {t.payment?.betaBannerBody || 'You will not be charged. Activate to unlock everything.'}
              </p>
            </div>
          )}

          {/* Plan selection — hidden during Beta */}
          {!isBeta && (
          <div className="space-y-2 mb-4">
            {/* Yearly — BEST VALUE */}
            <button
              onClick={() => setSelectedPlan('yearly')}
              aria-label={`Select yearly plan at ${planPrice('yearly')} per year`}
              aria-pressed={selectedPlan === 'yearly'}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left relative overflow-hidden ${
                selectedPlan === 'yearly'
                  ? 'border-[var(--accent-gold)] bg-[var(--accent-gold)]/10'
                  : 'border-[var(--border)] hover:border-[var(--accent-gold)]/50'
              }`}
            >
              <div className="absolute top-0 right-0 bg-[var(--accent-teal)] text-xs font-bold text-white px-2 py-0.5 rounded-bl-lg">
                {t.payment?.bestValue || 'BEST VALUE'}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[var(--text-primary)]">
                    {t.payment?.yearly || 'Yearly'}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                    {t.payment?.billedAnnually || 'Billed annually — cancel any time in the app'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[var(--accent-gold)]">{planPrice('yearly')}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{t.payment?.perYear || '/year'}</p>
                </div>
              </div>
            </button>

            {/* Monthly */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              aria-label={`Select monthly plan at ${planPrice('monthly')} per month`}
              aria-pressed={selectedPlan === 'monthly'}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                selectedPlan === 'monthly'
                  ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/10'
                  : 'border-[var(--border)] hover:border-[var(--accent-teal)]/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[var(--text-primary)]">
                    {t.payment?.monthly || 'Monthly'}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                    {t.payment?.billedMonthly || 'Billed monthly — cancel any time in the app'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[var(--accent-teal)]">{planPrice('monthly')}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{t.payment?.perMonth || '/month'}</p>
                </div>
              </div>
            </button>

            {/* Trip Pass — one-off */}
            <button
              onClick={() => setSelectedPlan('trip_pass')}
              aria-label={`Select trip pass at ${planPrice('trip_pass')} one-off`}
              aria-pressed={selectedPlan === 'trip_pass'}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                selectedPlan === 'trip_pass'
                  ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/10'
                  : 'border-[var(--border)] hover:border-[var(--accent-teal)]/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[var(--text-primary)]">
                    {t.payment?.tripPass || 'Trip Pass'}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                    {t.payment?.tripPassDescription || '7 days of premium — buy when you travel'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[var(--accent-teal)]">{planPrice('trip_pass')}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{t.payment?.oneOff || 'one-off'}</p>
                </div>
              </div>
            </button>
          </div>
          )}

          {failed && (
            <p role="alert" className="mb-3 text-sm text-center text-[var(--status-red)]">
              {t.payment.upgradeFailed}
            </p>
          )}

          <button
            onClick={handleUpgrade}
            disabled={processing}
            aria-label={isBeta ? 'Activate Free Beta' : 'Upgrade now'}
            className="w-full py-4 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[var(--accent-gold)] to-amber-500 text-black hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5 active:translate-y-0"
          >
            {processing ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                {t.payment?.processing || 'Processing...'}
              </span>
            ) : isBeta ? (
              t.payment?.activateFreeBeta || 'Activate Free Beta'
            ) : (
              t.payment?.upgradeNow || 'Upgrade Now'
            )}
          </button>

          {/* Why we charge — hidden in Beta (nothing is being charged) */}
          {!isBeta && (
            <div className="mt-4 p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)]">
              <div className="flex items-start gap-2">
                <Info className="w-3.5 h-3.5 text-[var(--text-secondary)]/60 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] leading-relaxed text-[var(--text-secondary)]/60">
                  {t.payment?.whyWeCharge || 'Google charges us, every time we use the basic data that\'s free in Maps. We add AI, Taste Learning, Practicality prioritisation, and more. We can only hold Google data for a few days. Sharing this app with others, and your financial support, keeps us running. And we think that\'s cool.'}
                </p>
              </div>
            </div>
          )}

          <p className="text-xs text-center text-[var(--text-secondary)]/60 mt-3">
            {isBeta ? (t.payment?.betaActivate || 'Activates instantly — no payment required') : (t.payment?.securePayment || 'Secure checkout')}
          </p>
        </div>
      </div>
    </div>
  );
}
